import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';

import examApis from '../../../apis/exams';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect';
import { academicYears } from '../../../constants/academicYears';
import { SnackbarSeverityType } from '../../../enums';
import { SchoolClassesConfig } from '../../../types/schoolConfigType';
import { StyledButton } from '../styled';

interface CloneExamsModalProps {
  open: boolean;
  onClose: () => void;
  academicYear: string;
  grade: string;
  division: string;
  term: { name: string; id: string } | null;
  allClasses?: SchoolClassesConfig;
  school: string;
  onExamsCloned: () => void;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
}

const CloneExamsModal: React.FC<CloneExamsModalProps> = ({
  open,
  onClose,
  academicYear,
  grade,
  division,
  term,
  allClasses,
  school,
  onExamsCloned,
  showToast
}) => {
  const [cloneAcademicYear, setCloneAcademicYear] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [cloneGrade, setCloneGrade] = useState('');
  const [cloneDivision, setCloneDivision] = useState('');
  const [cloneTerm, setCloneTerm] = useState<{
    name: string;
    id: string;
  } | null>(null);
  const [cloneTermOptions, setCloneTermOptions] = useState<
    { name: string; id: string }[]
  >([]);
  const [cloneGradeOptions, setCloneGradeOptions] = useState<string[]>([]);
  const [cloneDivisionOptions, setCloneDivisionOptions] = useState<string[]>(
    []
  );
  const [isCloneTermsLoading, setIsCloneTermsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setCloneAcademicYear('');
      setCloneGrade('');
      setCloneDivision('');
      setCloneTerm(null);
      setCloneTermOptions([]);
      setCloneDivisionOptions([]);
      setIsCloneTermsLoading(false);
      setIsLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (allClasses) {
      setCloneGradeOptions(allClasses.grades || []);
    }
  }, [allClasses]);

  useEffect(() => {
    if (allClasses && cloneGrade) {
      const divisions = allClasses.classes
        .filter((schoolClass) => schoolClass.grade === cloneGrade)
        .map((schoolClass) => schoolClass.division);
      setCloneDivisionOptions(divisions);
    } else if (allClasses) {
      setCloneDivisionOptions(allClasses.divisions || []);
    }
  }, [allClasses, cloneGrade]);

  useEffect(() => {
    if (cloneGrade && cloneDivision && cloneAcademicYear && school) {
      setIsCloneTermsLoading(true);
      examApis
        .getTermsFor(cloneGrade, cloneDivision, cloneAcademicYear, school)
        .then((response: any) => {
          const apiTerms = Array.isArray(response) ? response : [];
          setCloneTermOptions(
            apiTerms.map((t: any) => ({ name: t.name, id: t.id }))
          );
        })
        .catch((error) => {
          console.error(error);
          showToast(
            'Failed to load terms for cloning.',
            SnackbarSeverityType.error
          );
        })
        .finally(() => setIsCloneTermsLoading(false));
    } else {
      setCloneTermOptions([]);
      setCloneTerm(null);
    }
  }, [
    cloneGrade,
    cloneDivision,
    cloneAcademicYear,
    school,
    allClasses,
    showToast
  ]);

  const handleClone = async () => {
    setIsLoading(true);
    try {
      await examApis.cloneExams(school, {
        cloneFrom: {
          academicYear: cloneAcademicYear,
          grade: cloneGrade,
          division: cloneDivision,
          termId: cloneTerm?.id as unknown as string
        },
        cloneTo: {
          academicYear: academicYear,
          grade: grade,
          division: division,
          termId: term?.id as unknown as string
        }
      });
      onExamsCloned();
      onClose();
      setCloneAcademicYear('');
      setCloneGrade('');
      setCloneDivision('');
      setCloneTerm(null);
      showToast('Exams cloned successfully!', SnackbarSeverityType.success);
    } catch (error) {
      console.error('Error cloning exams:', error);
      showToast('Failed to clone exams.', SnackbarSeverityType.error);
    } finally {
      setIsLoading(false);
    }
  };

  const isCloneButtonDisabled =
    !academicYear ||
    !grade ||
    !division ||
    !term?.id ||
    !cloneAcademicYear ||
    !cloneGrade ||
    !cloneDivision ||
    !cloneTerm?.id ||
    !school;

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Clone Exams'
      actions={
        <>
          <Button onClick={onClose} color='primary' variant='contained'>
            Cancel
          </Button>
          <StyledButton
            onClick={handleClone}
            disabled={isLoading || isCloneButtonDisabled}
          >
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Clone'
            )}
          </StyledButton>
        </>
      }
    >
      <Typography variant='body2' sx={{ mb: 1 }}>
        Note: You are cloning exams for{' '}
        <Box component='span' sx={{ fontWeight: 'bold' }}>
          {academicYear}
        </Box>{' '}
        for{' '}
        <Box component='span' sx={{ fontWeight: 'bold' }}>
          {term?.name}
        </Box>{' '}
        of{' '}
        <Box component='span' sx={{ fontWeight: 'bold' }}>
          {grade}-{division}
        </Box>
      </Typography>
      <Typography variant='h6' sx={{ mb: 1 }}>
        Clone from
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DropdownSelect
          label='Academic Year'
          placeholder='Select Academic Year'
          name='cloneAcademicYear'
          value={cloneAcademicYear}
          options={academicYears}
          onChange={(e) => {
            setCloneAcademicYear(e.target.value as string);
            setCloneGrade('');
            setCloneDivision('');
            setCloneTerm(null);
            setCloneDivisionOptions([]);
            setCloneTermOptions([]);
          }}
        />
        <DropdownSelect
          label='Grade'
          placeholder='Select Grade'
          name='cloneGrade'
          value={cloneGrade}
          options={cloneGradeOptions.map((g) => ({ value: g, label: g }))}
          onChange={(e) => {
            setCloneGrade(e.target.value as string);
            setCloneDivision('');
            setCloneTerm(null);
            setCloneDivisionOptions([]);
            setCloneTermOptions([]);
          }}
          loading={cloneGradeOptions.length === 0 && !!allClasses}
        />
        <DropdownSelect
          label='Division'
          placeholder='Select Division'
          name='cloneDivision'
          value={cloneDivision}
          options={cloneDivisionOptions.map((d) => ({ value: d, label: d }))}
          onChange={(e) => {
            setCloneDivision(e.target.value as string);
            setCloneTerm(null);
            setCloneTermOptions([]);
          }}
          loading={cloneDivisionOptions.length === 0 && !!cloneGrade}
        />
        <DropdownSelect
          label='Term'
          placeholder='Select Term'
          name='cloneTerm'
          value={cloneTerm?.id || ''}
          options={cloneTermOptions.map((t) => ({
            value: t.id,
            label: t.name
          }))}
          onChange={(e) =>
            setCloneTerm(
              cloneTermOptions.find((t) => t.id === e.target.value) || null
            )
          }
          loading={isCloneTermsLoading}
        />
      </Box>
    </DialogModal>
  );
};

export default CloneExamsModal;
